<template>
  <div class="dashboard-entities-view">
    <!-- Title and create entity button -->
    <div class="dashboard-entities-view__title">
      <h3>{{ TextAssistant.entityPluralName(entityType) }}</h3>
      <template v-if="entitiesExist">
        <new-mailbox-button v-if="entityType === EEntities.MAILBOXES" :class="AppSumoAssistant.getNewEntityButtonMarkerClass(entityType)" size="medium" />
        <new-project-button v-else :class="AppSumoAssistant.getNewEntityButtonMarkerClass(entityType)" size="medium" />
      </template>
    </div>

    <!-- Entities exist -->
    <div v-if="entitiesExist">
      <!-- Grid view -->
      <div v-show="showGrid" class="dashboard-entities-view__grid-wrapper">
        <div
          v-for="(entity, index) in orderedEntities"
          :key="index"
          class="col-lg-6 col-xl-4 dashboard-entities-view__grid-wrapper__col"
        >
          <dashboard-card-entity
            :entity="entity"
            :daily-activity-log="getDailyActivityLog(entity)"
            :profile="profile"
            :entity-type="entityType"
            @favorite-clicked="$emit('toggle-favorite', entity)"
            @entity-archived="$emit('entity-archived', entity)"
          />
        </div>
      </div>

      <!-- Table view -->
      <div v-show="showTable" class="dashboard-entities-view__table-wrapper">
        <dashboard-table-view
          :entities="orderedEntities"
          :profile="profile"
          :entity-type="entityType"
          @favorite-clicked="(entity) => $emit('toggle-favorite', entity)"
        />
      </div>
    </div>

    <!-- No entities exist for this account membership -->
    <dashboard-entities-empty v-if="noEntities" :entity-type="entityType" />

    <!-- No entities match for this search result -->
    <dashboard-entities-no-results v-if="noSearchResults" />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, PropType } from 'vue'

/* Mixins */
import entityTypeMixin from '@/mixins/entity-type.mixin'

/* Components */
import DashboardTableView from './dashboard-table-view.vue'
import DashboardCardEntity from './dashboard-card-entity.vue'
import DashboardEntitiesEmpty from './dashboard-entities-empty.vue'
import DashboardEntitiesNoResults from './dashboard-entities-no-results.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IProjectsOutputProjectForHome,
  IProjectDailyActivityLogsDailyActivityLog,
  IPersonBase,
} from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'DashboardEntitiesView',

  components: {
    DashboardTableView,
    DashboardCardEntity,
    DashboardEntitiesEmpty,
    DashboardEntitiesNoResults,
  },

  extends: entityTypeMixin,

  props: {
    /**
     * View Type: Grid or Table
     */
    viewType: {
      type: String,
      required: true,
    },
    entities: {
      type: Array as () => Array<IProjectsOutputProjectForHome>,
      required: true,
    },
    dailyActivityLogs: {
      type: Array as () => Array<IProjectDailyActivityLogsDailyActivityLog>,
      default: null,
    },
    profile: {
      type: Object as PropType<IPersonBase>,
      required: true,
    },
    isSearching: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['entity-archived', 'toggle-favorite'],

  data() {
    return {
      EEntities,
      AppSumoAssistant,
      TextAssistant,
    }
  },

  computed: {
    // Order by favorites first, then sorted...
    orderedEntities(): IProjectsOutputProjectForHome[] {
      return [...this.entities].sort((a, b) => {
        const aIsSample = this.$permissions.isSampleInternalProjectID(a.id)
        const bIsSample = this.$permissions.isSampleInternalProjectID(b.id)

        /* Favorites first */
        if (a.isFavorite && !b.isFavorite) {
          return -1
        }
        if (!a.isFavorite && b.isFavorite) {
          return 1
        }

        /* Samples second */
        if (aIsSample && !bIsSample) {
          return -1
        }
        if (!aIsSample && bIsSample) {
          return 1
        }

        /* Alphabetical third */
        return a.name > b.name ? 1 : -1
      })
    },
    noSearchResults(): boolean {
      return this.entities.length === 0 && this.isSearching
    },
    noEntities(): boolean {
      return this.entities.length === 0 && !this.isSearching
    },
    entitiesExist(): boolean {
      return !this.noEntities && !this.noSearchResults
    },
    showGrid(): boolean {
      return this.viewType === 'grid'
    },
    showTable(): boolean {
      return this.viewType === 'table'
    },
  },

  methods: {
    getDailyActivityLog(entity: IProjectsOutputProjectForHome) {
      if (this.dailyActivityLogs === null) {
        return null
      }
      return this.dailyActivityLogs.find(d => d.id === entity.id)
    },
  },
})
</script>

<style lang="scss" scoped>
.dashboard-entities-view {
  $dashboard-card-horizontal-padding: 8px;

  &__title {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;

    h3 {
      margin: 0;
    }
  }

  &__grid-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-left: -1 * $dashboard-card-horizontal-padding;
    margin-right: -1 * $dashboard-card-horizontal-padding;

    &__col {
      margin-bottom: 20px;
      padding: 0 $dashboard-card-horizontal-padding;

      .dashboard-card-entity {
        width: 100%;
      }
    }
  }

  &__table-wrapper {
    padding-bottom: 18px;
  }
}
</style>
